import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTheme } from 'styled-components';

import {
    QUERIES_ADMIN,
    ROUTES_ACCOUNT,
    ROUTES_ADMIN,
} from '@constants';

import { useRealState } from 'contexts/RealStateContext';

import { Button, HelperBoolean, HelperOptions } from 'components/atoms';
import { Table } from 'components/organisms';
import { onErrorMessage, onSuccessMessage } from 'helpers';
import {
    getAdministratorPropertiesAttributesIndex,
    removeAdministratorPropertiesAttributes,
} from 'services';
import { sweetAlert } from 'utils';

import { PropertiesAttributesListWrap } from './styles';
import Layout from '../../Layout';

const PropertiesAttributesList = () => {
    const titleMessages = 'Atributos dos Imóveis!';
    const theme = useTheme();
    const navigate = useNavigate();
    const { getLinkWithSlug } = useRealState();

    const queryClient = useQueryClient();

    const { data, isLoading: isLoadingList } = useQuery(
        QUERIES_ADMIN.PROPERTIES_ATTRIBUTES.base,
        () => getAdministratorPropertiesAttributesIndex()
    );

    const { mutate: removeRegister } =
        useMutation((id: number) => removeAdministratorPropertiesAttributes(String(id)), {
            mutationKey: QUERIES_ADMIN.PROPERTIES_ATTRIBUTES.base,
            onSuccess: () => {
                onSuccessMessage(
                    titleMessages,
                    'Registro removido com sucesso!'
                );

                queryClient.invalidateQueries(QUERIES_ADMIN.PROPERTIES_ATTRIBUTES.base);
            },
            onError: (err) => {
                onErrorMessage(titleMessages, err);
            },
        });

    function onHandleNavigate(param: string) {
        navigate(param);
    }

    function newRegister() {
        onHandleNavigate(getLinkWithSlug(ROUTES_ADMIN.PROPERTIES_ATTRIBUTES.FORM.fullPath));
    }

    function editRegister(param: string) {
        onHandleNavigate(
            getLinkWithSlug(`${ROUTES_ADMIN.PROPERTIES_ATTRIBUTES.FORM.fullPath}/${param}`)
        );
    }

    function removeRegisterRequest(id: number) {
        sweetAlert
            .fire({
                title: 'Você tem certeza?',
                text: 'Se você confirmar este registro será removido e está ação não poderá ser revertida!',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: theme.colors.primary,
                cancelButtonColor: theme.colors.danger,
                confirmButtonText: 'Sim, remover registro!',
                cancelButtonText: 'Não, cliquei errado!',
            })
            .then((result) => {
                if (result.isConfirmed) {
                    removeRegister(id);
                }
            });
    }

    return (
        <Layout
            titlePage="Painel Administrativo"
            breadcrumbs={[
                {
                    label: 'Home',
                    href: getLinkWithSlug(ROUTES_ACCOUNT.SELECTED.fullPath),
                },
                {
                    label: 'Atributos dos Imóveis',
                    href: getLinkWithSlug(ROUTES_ADMIN.PROPERTIES_ATTRIBUTES.fullPath),
                },
            ]}
            title="Atributos dos Imóveis"
            buttonTitle={
                <Button color="nonary" onClick={() => newRegister()}>
                    Novo Registro
                </Button>
            }
        >
            <PropertiesAttributesListWrap className="properties-attributes-template">
                <Table
                    isLoading={isLoadingList}
                    columns={[
                        {
                            dataField: 'id',
                            text: 'ID',
                            sort: true,
                            style: { fontWeight: 700 }
                        },
                        {
                            dataField: 'descricao',
                            text: 'Descrição',
                            sort: true,
                            headerStyle: { width: '100%' }
                        },
                        {
                            dataField: 'icone',
                            text: 'Ícone',
                            sort: true,
                        },
                        {
                            dataField: 'tipo',
                            text: 'Tipo',
                            sort: true,
                        },
                        {
                            dataField: 'destaque',
                            text: 'Destaque',
                            sort: true,
                            formatter: (fieldValue) => (
                                <HelperBoolean value={fieldValue} />
                            ),
                            style: { width: '8.5rem' }
                        },
                        {
                            dataField: 'obrigatorio',
                            text: 'Obrigatório',
                            sort: true,
                            formatter: (fieldValue) => (
                                <HelperBoolean value={Boolean(fieldValue)} />
                            ),
                            style: { width: '10rem' }
                        },
                        {
                            dataField: 'id',
                            text: 'Opções',
                            formatter: (fieldValue) =>
                                HelperOptions({
                                    edit: true,
                                    editFunction: () =>
                                        editRegister(fieldValue),
                                    removeFunction: () =>
                                        removeRegisterRequest(fieldValue),
                                }),
                            headerStyle: { width: '20%' }
                        },
                    ]}
                    data={data}
                />
            </PropertiesAttributesListWrap>
        </Layout>
    );
};

export default PropertiesAttributesList;
